import type {
  PartnerUser,
  PartnerUserLocation,
} from '../../../api/services/lupp/onboarding/dto.onboarding';

export type UserLocation = {
  id: string;
  name: string;
  address: string;
  isPrimary?: boolean;
};

export type UserWorkspace = {
  id: string;
  name: string;
  isActive: boolean;
};

export type UserManagementUser = {
  id: string;
  name: string;
  email: string;
  role: string;
  locations: UserLocation[];
  workspaces: UserWorkspace[];
};

/** Builds a display address from a partner location's fields, skipping any that are blank. */
const formatLocationAddress = (location: PartnerUserLocation): string =>
  [
    location.addressLine1,
    location.addressLine2,
    location.city,
    location.stateProvince,
    location.postalCode,
  ]
    .filter(Boolean)
    .join(', ');

/** Maps a real partners API location record to the shape this page renders. */
export const toUserLocation = (location: PartnerUserLocation): UserLocation => {
  const address = formatLocationAddress(location);

  return {
    id: location.locationIdentifier,
    name: location.locationName || address || location.locationType || 'Unnamed location',
    address,
    isPrimary: location.isPrimary,
  };
};

/** Maps a real partners/users API record to the shape this page renders. */
export const toUserManagementUser = (user: PartnerUser): UserManagementUser => ({
  // user_id (Auth0-prefixed) is also accepted as business_partner_user_identifier
  // by PATCH /partners/{id}/user-locations.
  id: user.user_id,
  name: user.name,
  email: user.email,
  role: user.idpRoleName || '',
  locations: user.locations.map(toUserLocation),
  // Not yet returned by /partners/users — placeholder until a workspaces endpoint exists.
  workspaces: [],
});
